<div class="docpic" [class.kiosk]="kiosk">
  <ng-container *ngIf="!kiosk">
  <p class="intro">
    Point the camera at a document. When the frame is detected, the text is
    <strong>crisp</strong>, and it checks out as a real document, the photo is
    <strong>captured automatically</strong> with a beep — no tap needed. Or
    <strong>upload an image / PDF</strong> to run the same detection on a file.
  </p>

  <div class="controls">
    <mat-form-field appearance="outline" class="camera-select" subscriptSizing="dynamic">
      <mat-label>Camera</mat-label>
      <mat-select
        [value]="selectedCameraId"
        (valueChange)="selectedCameraId = $event"
        (opened)="loadCameras()"
        [disabled]="running">
        <mat-option
          *ngFor="let cam of cameras; let i = index; trackBy: trackByDeviceId"
          [value]="cam.deviceId">{{ cam.label || 'Camera ' + i }}</mat-option>
      </mat-select>
    </mat-form-field>

    <ng-container *ngIf="!running; else liveControls">
      <button mat-flat-button (click)="start()">
        <mat-icon>videocam</mat-icon> Start
      </button>
    </ng-container>
    <ng-template #liveControls>
      <button mat-flat-button (click)="capture()">
        <mat-icon>photo_camera</mat-icon> Take photo
      </button>
      <button mat-stroked-button (click)="stop()">
        <mat-icon>stop</mat-icon> Stop
      </button>
    </ng-template>

    <button
      mat-stroked-button
      [color]="autoCapture ? 'primary' : undefined"
      (click)="autoCapture = !autoCapture">
      <mat-icon>{{ autoCapture ? 'motion_photos_on' : 'motion_photos_off' }}</mat-icon>
      Auto-capture {{ autoCapture ? 'on' : 'off' }}
    </button>

    <button mat-stroked-button (click)="fileInput.click()" [disabled]="identifying">
      <mat-icon>upload_file</mat-icon> Upload image / PDF
    </button>
    <input
      #fileInput
      type="file"
      accept="image/*,application/pdf"
      hidden
      (change)="processFile($event)" />
  </div>

  <p class="processing" *ngIf="identifying && !running"><mat-icon>hourglass_top</mat-icon> Processing file…</p>
  </ng-container>

  <div class="kiosk-upload" *ngIf="kiosk && source === 'upload'">
    <ng-container *ngIf="identifying; else kioskUploadBtn">
      <p class="processing">
        <mat-spinner diameter="20"></mat-spinner> Processando o documento…
      </p>
    </ng-container>
    <ng-template #kioskUploadBtn>
      <button mat-flat-button class="upload-btn" (click)="fileInput.click()">
        <mat-icon>upload_file</mat-icon> Escolher arquivo
      </button>
    </ng-template>
    <input
      #fileInput
      type="file"
      accept="image/*,application/pdf"
      hidden
      (change)="processFile($event)" />
  </div>

  <div class="viewport" [class.live]="running" [hidden]="kiosk && source === 'upload'"
       (click)="onPreviewTap($event)">
    <video #video muted autoplay playsinline></video>
    <canvas #canvas hidden></canvas>
    <app-focus-ring [point]="focusRingPoint"></app-focus-ring>

    <ng-container *ngIf="running && overlayBox as box">
      <div
        class="doc-frame"
        [class]="overlayState"
        [style.left.%]="box.x * 100"
        [style.top.%]="box.y * 100"
        [style.width.%]="box.w * 100"
        [style.height.%]="box.h * 100">
      </div>
    </ng-container>

    <span class="hint" *ngIf="running && !kiosk">{{ statusText }}</span>

    <!-- Manual-capture fallback: auto-capture ran MANUAL_FALLBACK_MS without a
         capture. Escape hatch only — auto-capture keeps running underneath and
         hides this the moment it recovers. -->
    <button
      *ngIf="running && manualFallbackVisible && !processing"
      mat-flat-button
      color="primary"
      class="manual-capture-btn"
      (click)="capture()">
      <mat-icon>photo_camera</mat-icon> Tirar foto
    </button>

    <!-- "Thinking" overlay while the capture is being read/identified. -->
    <div class="processing-overlay" *ngIf="processing">
      <mat-spinner diameter="56" strokeWidth="5"></mat-spinner>
      <span class="processing-overlay__text">{{ processingText }}</span>
    </div>
  </div>

  <div class="metrics" *ngIf="!kiosk && running && analysis as a">
    <span class="metric" [class.ok]="a.found">
      <mat-icon>{{ a.found ? 'check_circle' : 'crop_free' }}</mat-icon> Frame
    </span>
    <span class="metric" [class.ok]="a.isDocument">
      <mat-icon>{{ a.isDocument ? 'check_circle' : 'description' }}</mat-icon> Document
    </span>
    <span class="metric" [class.ok]="a.legible">
      <mat-icon>{{ a.legible ? 'check_circle' : 'zoom_in' }}</mat-icon> Size
    </span>
    <span class="metric" [class.ok]="a.crisp">
      <mat-icon>{{ a.crisp ? 'check_circle' : 'blur_on' }}</mat-icon>
      Sharpness {{ a.sharpness | number: '1.0-0' }}
    </span>
  </div>

  <!-- Camera failure (#5): friendly, translated message + fallback to upload. -->
  <div class="camera-error" *ngIf="cameraFailed; else plainError">
    <mat-icon>videocam_off</mat-icon>
    <p class="camera-error__msg">{{ error }}</p>
    <div class="camera-error__actions">
      <button mat-flat-button (click)="useUploadInstead()">
        <mat-icon>upload_file</mat-icon> {{ uploadInsteadLabel }}
      </button>
      <button mat-stroked-button (click)="start()">
        <mat-icon>refresh</mat-icon> {{ tryAgainLabel }}
      </button>
    </div>
  </div>
  <ng-template #plainError>
    <div class="error" *ngIf="error">{{ error }}</div>
  </ng-template>

  <div class="shot" *ngIf="!kiosk && photo as shot">
    <h3>Captured document</h3>
    <p class="doc-type identifying" *ngIf="identifying; else docTypeBlock">
      <mat-icon>hourglass_top</mat-icon> Identifying document type…
    </p>
    <ng-template #docTypeBlock>
      <ng-container *ngIf="docType as dt">
        <p class="doc-type" [class.unknown]="dt.type === 'unknown'">
          <mat-icon>{{ dt.type === 'unknown' ? 'help_outline' : 'badge' }}</mat-icon>
          {{ dt.label }}
          <span class="conf" *ngIf="dt.type !== 'unknown'">({{ dt.confidence }} · {{ dt.source }})</span>
        </p>
        <p class="experts" *ngIf="dt.experts?.length">experts → {{ expertSummary(dt) }}</p>
        <details class="cnn-debug" open *ngIf="cnnDebug as dbg">
          <summary>Pipeline step by step &amp; scores</summary>
          <div class="dbg-strip">
            <figure>
              <figcaption>raw</figcaption>
              <img [src]="dbg.raw" alt="raw capture" />
            </figure>
            <figure *ngFor="let s of dbg.stages; trackBy: trackByStageName">
              <figcaption>{{ s.name }}<br /><b>{{ s.method }}</b></figcaption>
              <img [src]="s.img" [alt]="s.name" />
            </figure>
            <figure class="fed">
              <figcaption>fed to model<br />(rot {{ dbg.chosenDeg }}°)</figcaption>
              <img [src]="dbg.fed" alt="image fed to model" />
            </figure>
          </div>
          <table class="dbg-scores">
            <tr>
              <td>top prob</td>
              <td>{{ dbg.topProb | number: '1.3-3' }} (reject &lt; {{ dbg.oodMsp | number: '1.3-3' }})</td>
            </tr>
            <tr>
              <td>energy</td>
              <td>{{ dbg.energy | number: '1.2-2' }} (reject &gt; {{ dbg.oodEnergy | number: '1.2-2' }})</td>
            </tr>
            <tr>
              <td>verdict</td>
              <td>{{ dbg.ood ? 'OUT-OF-DISTRIBUTION → Not recognized' : 'accepted' }}</td>
            </tr>
          </table>
          <p class="dbg-rot">
            rotations tried:
            <span *ngFor="let r of dbg.rotations; trackBy: trackByRotationDeg">{{ r.deg }}°→{{ r.label }} {{ r.conf | number: '1.2-2' }}</span>
          </p>
        </details>
      </ng-container>
    </ng-template>
    <p class="ocr-result" *ngIf="ocr as o">
      <mat-icon>{{ o.legible ? 'task_alt' : 'visibility_off' }}</mat-icon>
      OCR read {{ o.goodWords }} clear words ({{ o.words }} total,
      {{ o.confidence | number: '1.0-0' }}% mean confidence)
    </p>
    <img [src]="shot" alt="Captured document" />
    <div class="shot-actions">
      <a mat-flat-button [href]="shot" download="docpic.jpg">
        <mat-icon>download</mat-icon> Download
      </a>
      <button mat-stroked-button (click)="retake()">
        <mat-icon>replay</mat-icon> Retake
      </button>
    </div>
  </div>
</div>
